import { FieldTypeAttributes } from './constants';

/** Raw API/state shape: category-keyed, with customFields and optional savedValue / field_type_id */
export type RawCustomDataByCategory = Record<
  string,
  {
    id?: number | string;
    name?: string;
    customFields?: Record<
      string,
      {
        cat_id?: string | null;
        field_type_id?: number | string;
        savedValue?: { value: string[] | string };
        text?: string | null | Array<{ name: string; status?: boolean }>;
        [key: string]: unknown;
      }
    >;
  }
>;

/** Value for a single field: text/dropdown (string or null) or multi-select (array of { name, status }) */
export type CustomFieldValue =
  | string
  | null
  | Array<{ name: string; status: boolean }>;

/**
 * Prepares raw custom data for display: deep clone, merge savedValue into .text where
 * .text is undefined, and normalize cat_id (null -> "0"). Run once at top level when
 * building initial/derived customData.
 */
export function prepareCustomDataForDisplay(
  raw: RawCustomDataByCategory | null | undefined
): RawCustomDataByCategory {
  if (!raw || typeof raw !== 'object') return raw ?? {};
  const out = JSON.parse(JSON.stringify(raw)) as RawCustomDataByCategory;
  const MULTI_SELECT = FieldTypeAttributes.ATTRIBUTE_MULTI_SELECT;

  Object.keys(out).forEach((catId) => {
    const cat = out[catId];
    if (!cat?.customFields) return;

    Object.keys(cat.customFields).forEach((fieldId) => {
      const f = cat.customFields![fieldId];
      if (f.cat_id == null) f.cat_id = '0';
      if (f.text !== undefined) return;
      if (!f.savedValue || !f.savedValue.value) return;

      const val = f.savedValue.value;
      const arr = Array.isArray(val) ? val : [val];
      if (Number(f.field_type_id) === MULTI_SELECT) {
        f.text = arr.map((v) =>
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          (typeof v === 'object' && v != null && 'name' in v
            ? { name: (v as { name: string }).name, status: true }
            : { name: String(v), status: true }));
      } else {
        const first = arr[0];
        f.text =
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
          typeof first === 'object' && (first != null) && 'name' in first
            ? (first as { name: string }).name
            : (first ?? '');
      }
    });
  });

  return out;
}

/**
 * Returns a new customData object with one field's .text updated. Does not mutate input.
 */
export function setCustomFieldValue(
  customData: RawCustomDataByCategory | null | undefined,
  catId: string | null | undefined,
  fieldId: string,
  value: CustomFieldValue
): RawCustomDataByCategory {
  if (!customData || typeof customData !== 'object') return customData ?? {};
  const resolvedCatId = catId == null ? '0' : String(catId);
  const cat = customData[resolvedCatId];
  if (!cat?.customFields || !(fieldId in cat.customFields)) return { ...customData };
  return {
    ...customData,
    [resolvedCatId]: {
      ...cat,
      customFields: {
        ...cat.customFields,
        [fieldId]: { ...cat.customFields[fieldId], text: value },
      },
    },
  };
}
